interface OrganizationData {
  type: 'Organization'
  name: string
  url: string
  logo?: string
  sameAs?: string[]
}

interface SoftwareApplicationData {
  type: 'SoftwareApplication'
  name: string
  description: string
  url: string
  applicationCategory?: string
  operatingSystem?: string
  price?: string
  priceCurrency?: string
}

interface WebPageData {
  type: 'WebPage'
  name: string
  description: string
  url: string
}

interface FAQData {
  type: 'FAQPage'
  questions: { question: string; answer: string }[]
}

type StructuredDataType = OrganizationData | SoftwareApplicationData | WebPageData | FAQData

interface StructuredDataProps {
  data: StructuredDataType
}

function generateOrganizationSchema(data: OrganizationData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: data.name,
    url: data.url,
    logo: data.logo,
    sameAs: data.sameAs,
  }
}

function generateSoftwareApplicationSchema(data: SoftwareApplicationData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: data.name,
    applicationCategory: data.applicationCategory || 'BusinessApplication',
    operatingSystem: data.operatingSystem || 'Web',
    description: data.description,
    url: data.url,
    provider: {
      '@type': 'Organization',
      name: 'WealthSpace Ltd',
      url: 'https://www.wealthspace.co.uk',
    },
    ...(data.price && {
      offers: {
        '@type': 'Offer',
        price: data.price,
        priceCurrency: data.priceCurrency || 'GBP',
        priceValidUntil: '2026-12-31',
      },
    }),
  }
}

function generateWebPageSchema(data: WebPageData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: data.name,
    description: data.description,
    url: data.url,
    publisher: {
      '@type': 'Organization',
      name: 'WealthSpace Ltd',
      url: 'https://www.wealthspace.co.uk',
    },
  }
}

function generateFAQSchema(data: FAQData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }
}

function generateSchema(data: StructuredDataType) {
  switch (data.type) {
    case 'Organization':
      return generateOrganizationSchema(data)
    case 'SoftwareApplication':
      return generateSoftwareApplicationSchema(data)
    case 'WebPage':
      return generateWebPageSchema(data)
    case 'FAQPage':
      return generateFAQSchema(data)
    default:
      return null
  }
}

export default function StructuredData({ data }: StructuredDataProps) {
  const schema = generateSchema(data)

  if (!schema) return null

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}

// Pre-configured structured data for common pages
export function HomePageStructuredData() {
  return (
    <StructuredData
      data={{
        type: 'SoftwareApplication',
        name: 'WealthSpace',
        description:
          'AI-powered practice management software for UK financial advisers. Generate suitability reports, manage client portals, and ensure FCA compliance.',
        url: 'https://www.wealthspace.co.uk',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        price: '134',
        priceCurrency: 'GBP',
      }}
    />
  )
}

export function OrganizationStructuredData() {
  return (
    <StructuredData
      data={{
        type: 'Organization',
        name: 'WealthSpace Ltd',
        url: 'https://www.wealthspace.co.uk',
        logo: 'https://www.wealthspace.co.uk/og-image.png',
      }}
    />
  )
}
